import {
  DEFAULT_TEXT_LABEL_FONT_SIZE,
  normalizeTextLabelFontFamily,
  normalizeTextLabelFontSize,
  type LineLabelStyle,
} from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useCanvasUi } from "../../context/CanvasUiContext";
import { LineLabelFloatingToolbar } from "../LineLabelFloatingToolbar";
import { DEFAULT_LABEL_COLOR } from "./CanvasColorPalettes";
import { markOf } from "./canvasColorRoles";
import { CanvasFloatingToolbarPortal } from "./CanvasFloatingToolbarPortal";

export const LINE_LABEL_PLACEHOLDER = "Add text";

/** Every field present, defaults filled in: Inter, 16px, ink, regular. */
export type ResolvedLineLabelStyle = {
  font_family: string;
  font_size: string;
  text_color: string;
  is_bold: boolean;
  is_strikethrough: boolean;
};

/** Anything shaped like a style: the stored one, or a resolved one being re-normalised. */
type LineLabelStyleInput = {
  font_family?: string;
  font_size?: string;
  text_color?: string;
  is_bold?: boolean;
  is_strikethrough?: boolean;
};

export function resolveLineLabelStyle(
  raw: LineLabelStyleInput | null | undefined,
): ResolvedLineLabelStyle {
  return {
    font_family: normalizeTextLabelFontFamily(raw?.font_family),
    font_size: normalizeTextLabelFontSize(raw?.font_size),
    text_color: raw?.text_color || DEFAULT_LABEL_COLOR,
    is_bold: !!raw?.is_bold,
    is_strikethrough: !!raw?.is_strikethrough,
  };
}

/**
 * The stored shape of a style: only what differs from the default is kept, so
 * an untouched label stores nothing. Values are normalised to the schema's
 * vocabulary first (the typeface menu's Modern is Inter).
 */
export function storableLineLabelStyle(
  style: ResolvedLineLabelStyle,
): LineLabelStyle | null {
  const normalized = resolveLineLabelStyle(style);
  const defaults = resolveLineLabelStyle(null);
  const out: LineLabelStyle = {};
  if (normalized.font_family !== defaults.font_family) {
    out.font_family = normalized.font_family as LineLabelStyle["font_family"];
  }
  if (normalized.font_size !== DEFAULT_TEXT_LABEL_FONT_SIZE) {
    out.font_size = normalized.font_size;
  }
  if (normalized.text_color !== DEFAULT_LABEL_COLOR) {
    out.text_color = normalized.text_color;
  }
  if (normalized.is_bold) out.is_bold = true;
  if (normalized.is_strikethrough) out.is_strikethrough = true;
  return Object.keys(out).length > 0 ? out : null;
}

export function lineLabelFontStyle(
  style: ResolvedLineLabelStyle,
): CSSProperties {
  return {
    fontFamily: style.font_family,
    fontSize: style.font_size,
    fontWeight: style.is_bold ? 600 : 400,
    textDecoration: style.is_strikethrough ? "line-through" : "none",
    // A line label sits on the ground: its colour is the family's mark.
    color:
      style.text_color === DEFAULT_LABEL_COLOR
        ? undefined
        : markOf(style.text_color),
  };
}

// Figma-style label: plain text on the canvas ground (bg-n-05, the pane's own
// token in both themes) so it occludes the line behind it, no pill chrome.
const chipClassName =
  "bg-n-05 text-foreground leading-none rounded-[3px] px-1 py-0.5 whitespace-nowrap";

interface LineLabelEditorProps {
  label: string;
  labelStyle: LineLabelStyle | null | undefined;
  editing: boolean;
  canEdit: boolean;
  /** Flow-space point the label sits on; the label bar floats above it. */
  midpoint: { x: number; y: number };
  onStartEdit: () => void;
  onEndEdit: () => void;
  /** Called with the trimmed draft; an empty string means "remove the label". */
  onCommit: (text: string) => void;
  /** Null clears the stored style back to the default. */
  onStyleChange: (style: LineLabelStyle | null) => void;
}

/**
 * The text at a line's midpoint — an edge's or a free connector's — and its
 * edit session: the chip at rest, the input while editing, and the label bar
 * floating above it. The parent positions this at the midpoint and owns the
 * "editing" flag; everything about the session lives here, once.
 */
export function LineLabelEditor({
  label,
  labelStyle,
  editing,
  canEdit,
  midpoint,
  onStartEdit,
  onEndEdit,
  onCommit,
  onStyleChange,
}: LineLabelEditorProps) {
  const style = resolveLineLabelStyle(labelStyle);
  const fontStyle = lineLabelFontStyle(style);
  const { setToolbarPopoverOpen } = useCanvasUi();
  const inputRef = useRef<HTMLInputElement | null>(null);
  // True while one of the bar's menus is open: the input loses focus to the
  // menu, and that blur must not end the session.
  const popoverOpenRef = useRef(false);
  const [draft, setDraft] = useState(label);
  // Escape discards the draft; tearing the input down fires blur, which would
  // otherwise commit what was meant to be thrown away.
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!editing) return;
    cancelledRef.current = false;
    setDraft(label);
    const raf = requestAnimationFrame(() => inputRef.current?.select());
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // A closing menu hands the caret back, so the next key lands in the text.
  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      popoverOpenRef.current = open;
      setToolbarPopoverOpen(open);
      if (!open) requestAnimationFrame(() => inputRef.current?.focus());
    },
    [setToolbarPopoverOpen],
  );

  const commit = (text: string) => {
    onCommit(text.trim());
    onEndEdit();
  };

  if (!editing) {
    return (
      <div
        role={canEdit ? "button" : undefined}
        style={fontStyle}
        className={cn(chipClassName, canEdit && "cursor-text")}
        onClick={(e) => {
          if (!canEdit) return;
          e.stopPropagation();
          onStartEdit();
        }}
      >
        {label}
      </div>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (popoverOpenRef.current) return;
          if (cancelledRef.current) onEndEdit();
          else commit(draft);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") commit(draft);
          if (e.key === "Escape") {
            cancelledRef.current = true;
            onEndEdit();
          }
        }}
        placeholder={LINE_LABEL_PLACEHOLDER}
        style={{
          ...fontStyle,
          // Wide enough for the placeholder when empty; +1ch for the caret.
          width: `${Math.max(draft.length, LINE_LABEL_PLACEHOLDER.length) + 1}ch`,
        }}
        className={cn(
          chipClassName,
          "text-center outline-none ring-1 ring-canvas-selection/70",
        )}
      />
      {canEdit && (
        <CanvasFloatingToolbarPortal
          positionAbsoluteX={midpoint.x}
          positionAbsoluteY={midpoint.y - 12}
          width={0}
          topOffset={64}
        >
          <LineLabelFloatingToolbar
            style={style}
            onStyleChange={(patch) =>
              onStyleChange(storableLineLabelStyle({ ...style, ...patch }))
            }
            onPopoverOpenChange={handlePopoverOpenChange}
          />
        </CanvasFloatingToolbarPortal>
      )}
    </>
  );
}
